import React, { useCallback, useEffect, useRef } from 'react'
import { create } from 'zustand'

import type { FileAPIEntry } from 'components/common/FileAPI'
import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import { logger } from 'utils/logging'

import { usePfStore } from '../store'
import { listOrderDocuments, listSessionFiles } from './sessionFiles.transport'
import {
  useBuildingShapeUpload,
  useConstructionDescUpload,
  useExteriorViewUpload,
  useHeaterNameplateUpload
} from './kinds/additionalInformation'
import { usePoaMainUpload, usePoaOtherUpload, usePoaRepsUpload } from './kinds/powerOfAttorney'

/** The configuration fields that record a stored session document, per page. */
export const DOCUMENT_MARKER_FIELDS: Partial<Record<ConfigurationFlowId, readonly string[]>> = {
  power_of_attorney: ['powerOfAttorneyFilename', 'representativePowerOfAttorneyFilename', 'otherFilenames'],
  consumption_certificate: ['exteriorViewFilenames', 'heaterNameplateFilenames'],
  energy_certificate_of_need: ['exteriorViewFilenames', 'heaterNameplateFilenames', 'buildingShapeFilenames', 'constructionDescriptionFilenames'],
}

const SESSION_PREFIX = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/i

/** The session a stored filename was uploaded under (`<sessionId>-backbonephoto-001.pdf`), or `null`. */
export const sessionIdOfStoredFilename = (filename: string): string | null => SESSION_PREFIX.exec(filename)?.[1] ?? null

/** A filename that names another session. One with no session prefix is not attributable and never foreign. */
const isForeign = (filename: string, sessionId: string): boolean => {
  const owner = sessionIdOfStoredFilename(filename)
  return owner !== null && owner.toLowerCase() !== sessionId.toLowerCase()
}

interface DocumentMarkers {
  cartItemId: string
  flowId: ConfigurationFlowId
  dto: Record<string, unknown>
  filenames: string[]
}

/** Every configuration that records a document, with the filenames it records. */
export const documentMarkersOf = (configs: Readonly<Record<string, unknown>>): DocumentMarkers[] => {
  const result: DocumentMarkers[] = []
  for (const [key, dto] of Object.entries(configs)) {
    const separator = key.indexOf('|')
    const flowId = key.slice(separator + 1) as ConfigurationFlowId
    const fields = DOCUMENT_MARKER_FIELDS[flowId]
    if (!fields || !dto || typeof dto !== 'object') continue
    const filenames = fields.flatMap((field) => {
      const value = (dto as Record<string, unknown>)[field]
      if (typeof value === 'string') return value ? [value] : []
      return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
    })
    if (filenames.length > 0) result.push({ cartItemId: key.slice(0, separator), flowId, dto: dto as Record<string, unknown>, filenames })
  }
  return result
}

/** The configuration with the given filenames removed from its document fields. */
export const withoutDocumentMarkers = (
  dto: Record<string, unknown>,
  flowId: ConfigurationFlowId,
  gone: ReadonlySet<string>
): Record<string, unknown> => {
  const next = { ...dto }
  for (const field of DOCUMENT_MARKER_FIELDS[flowId] ?? []) {
    const value = next[field]
    if (typeof value === 'string' && gone.has(value)) next[field] = undefined
    else if (Array.isArray(value)) next[field] = value.filter((entry) => !(typeof entry === 'string' && gone.has(entry)))
  }
  return next
}

interface DroppedDocumentsState {
  sessionId: string | null
  keys: ReadonlySet<string>
  mark: (sessionId: string, keys: readonly string[]) => void
}

/** The pages that lost a recorded document under the current session, for the "uploaded in a previous session" notice. */
const useDroppedDocumentsStore = create<DroppedDocumentsState>((set) => ({
  sessionId: null,
  keys: new Set(),
  mark: (sessionId, keys) => set((state) => {
    const base = state.sessionId === sessionId ? state.keys : new Set<string>()
    return { sessionId, keys: new Set([...base, ...keys]) }
  }),
}))

/** Whether this page's recorded documents were dropped because the current session does not hold them. */
export const useSessionDocumentsDropped = (cartItemId: string, flowId: ConfigurationFlowId): boolean => {
  const sessionId = usePfStore((state) => state.draft.sessionId)
  return useDroppedDocumentsStore((state) => state.sessionId === sessionId && state.keys.has(`${cartItemId}|${flowId}`))
}

/** Clears the notices; for tests. */
export const resetDroppedSessionDocuments = () => useDroppedDocumentsStore.setState({ sessionId: null, keys: new Set() })

interface ReconciledScope {
  entries: ReadonlyArray<Pick<FileAPIEntry<never>, 'gcFilename'>>
  discard: (filenames: readonly string[]) => void
}

/**
 * Drops the document filenames recorded in the configuration (power of attorney, certificate photos) that
 * the order's session does not hold, from the configuration and from the given upload scopes. Completeness
 * reads those markers while the backend attaches only what the session holds, so a marker it does not
 * hold shows a document the order will not carry. Files of another session (a property change, a
 * rejected or expired session) are dropped as soon as they appear; the session's own listings are read
 * once per session. Dropping un-confirms the page and raises its restored-files notice.
 */
export function useSessionDocumentReconcile(scopes: readonly ReconciledScope[]): void {
  const sessionId = usePfStore((state) => state.draft.sessionId)
  const isHydrating = usePfStore((state) => state.draft.isHydrating)
  const productConfigs = usePfStore((state) => state.productConfigs)

  const scopesRef = useRef(scopes)
  scopesRef.current = scopes

  const drop = useCallback((session: string, gone: ReadonlySet<string>) => {
    const state = usePfStore.getState()
    const affected = documentMarkersOf(state.productConfigs).filter(({ filenames }) => filenames.some((name) => gone.has(name)))
    affected.forEach(({ cartItemId, flowId, dto }) =>
      state.patchConfig(cartItemId, flowId, withoutDocumentMarkers(dto, flowId, gone) as never))
    scopesRef.current.forEach((scope) => scope.discard([...gone]))
    if (affected.length > 0) {
      useDroppedDocumentsStore.getState().mark(session, affected.map(({ cartItemId, flowId }) => `${cartItemId}|${flowId}`))
    }
  }, [])

  useEffect(() => {
    if (!sessionId || isHydrating) return
    const foreign = new Set(documentMarkersOf(productConfigs).flatMap(({ filenames }) => filenames).filter((name) => isForeign(name, sessionId)))
    if (foreign.size > 0) drop(sessionId, foreign)
  }, [sessionId, isHydrating, productConfigs, drop])

  const foreignEntries = scopes.flatMap(({ entries }) => entries)
    .map((entry) => entry.gcFilename)
    .filter((name): name is string => Boolean(name && sessionId && isForeign(name, sessionId)))
  const foreignEntriesKey = foreignEntries.join('\n')
  useEffect(() => {
    if (!foreignEntriesKey) return
    scopesRef.current.forEach((scope) => scope.discard(foreignEntriesKey.split('\n')))
  }, [foreignEntriesKey])

  const verifiedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!sessionId || isHydrating || verifiedRef.current === sessionId) return
    verifiedRef.current = sessionId

    const expected = documentMarkersOf(usePfStore.getState().productConfigs)
      .flatMap(({ filenames }) => filenames)
      .filter((name) => !isForeign(name, sessionId))
    if (expected.length === 0) return

    const ctx = {
      getSessionId: () => sessionId,
      getWorkspaceId: () => usePfStore.getState().draft.sessionWorkspaceId,
      getEmail: () => null,
    }
    Promise.all([listSessionFiles(ctx), listOrderDocuments(ctx)])
      .then(([files, orderDocuments]) => {
        if (usePfStore.getState().draft.sessionId !== sessionId) return
        const held = new Set([
          ...Object.values(files.files ?? {}).flat().map((file) => file.filename),
          ...(orderDocuments.documents ?? []).map((document) => document.filename),
        ])
        const gone = new Set(expected.filter((name) => !held.has(name)))
        if (gone.size > 0) drop(sessionId, gone)
      })
      .catch((error) => {
        // Kept rather than dropped: placement still refuses a file the session recorded and lost.
        logger.warn('pf-revamp could not list session documents; keeping the recorded ones', error, {
          context: { component: 'SessionDocumentReconcile', action: 'verify' },
        })
      })
  }, [sessionId, isHydrating, drop])
}

/** Mount-point for {@link useSessionDocumentReconcile}; must sit inside the PoA and certificate upload providers. */
export const SessionDocumentReconcile: React.FC = () => {
  const poaMain = usePoaMainUpload()
  const poaReps = usePoaRepsUpload()
  const poaOther = usePoaOtherUpload()
  const exterior = useExteriorViewUpload()
  const nameplate = useHeaterNameplateUpload()
  const construction = useConstructionDescUpload()
  const buildingShape = useBuildingShapeUpload()
  useSessionDocumentReconcile([poaMain, poaReps, poaOther, exterior, nameplate, construction, buildingShape])
  return null
}
